import React from 'react'
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Shield, Phone, Mail, Globe, MapPin } from 'lucide-react'

interface PatternDistributionProps {
  detailed?: boolean
}

const COLORS = {
  critical: '#EF4444',
  high: '#F97316', 
  medium: '#EAB308',
  low: '#10B981'
}

const phonePatternData = [
  { name: 'Sequential', value: 312, color: COLORS.critical, percentage: 42 },
  { name: 'Random', value: 487, color: COLORS.low, percentage: 65 },
  { name: 'Repeated Digits', value: 45, color: COLORS.medium, percentage: 6 },
  { name: 'Invalid Format', value: 23, color: COLORS.high, percentage: 3 }
]

const emailDomainData = [
  { domain: 'armyspy.com', count: 234, risk: 'critical' },
  { domain: 'dayrep.com', count: 189, risk: 'high' },
  { domain: 'rhyta.com', count: 156, risk: 'high' },
  { domain: 'teleworm.us', count: 123, risk: 'medium' },
  { domain: 'jourrapide.com', count: 98, risk: 'medium' }
]

const ipTypeData = [
  { type: 'Residential', count: 1823, color: COLORS.low },
  { type: 'Data Center', count: 892, color: COLORS.critical },
  { type: 'VPN/Proxy', count: 234, color: COLORS.high },
  { type: 'Mobile', count: 156, color: COLORS.medium },
  { type: 'Unknown', count: 89, color: '#6B7280' }
]

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload
    return (
      <div className="bg-card border rounded-lg shadow-lg p-3">
        <p className="font-semibold text-foreground">{data.name || data.type || data.domain}</p>
        <p className="text-sm text-muted-foreground">
          Count: {data.value || data.count}
          {data.percentage && ` (${data.percentage}%)`}
        </p>
      </div>
    )
  }
  return null
}

export const PatternDistribution: React.FC<PatternDistributionProps> = ({ detailed = false }) => {
  if (detailed) {
    return (
      <div className="space-y-6">
        {/* Phone Patterns */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Phone className="h-5 w-5" />
              Phone Number Pattern Analysis
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={phonePatternData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percentage }) => `${name} ${percentage}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {phonePatternData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              
              <div className="space-y-3">
                <h4 className="font-semibold">Pattern Details</h4>
                {phonePatternData.map((pattern, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-muted/50 rounded">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: pattern.color }}
                      />
                      <span className="font-medium">{pattern.name}</span>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold">{pattern.value}</div>
                      <div className="text-sm text-muted-foreground">{pattern.percentage}%</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Email Domains */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Suspicious Email Domains
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={emailDomainData} layout="horizontal">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="domain" type="category" width={100} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" fill="#EF4444" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* IP Types */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              IP Address Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={ipTypeData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="type" />
                  <YAxis />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" fill="#EF4444" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5" />
          Pattern Distribution
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Phone Patterns Pie Chart */}
          <div>
            <h4 className="font-semibold mb-4">Phone Number Patterns</h4>
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={phonePatternData}
                    cx="50%"
                    cy="50%"
                    outerRadius={70}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, percentage }) => `${percentage}%`}
                  >
                    {phonePatternData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Top Suspicious Domains */}
          <div>
            <h4 className="font-semibold mb-4">Top Suspicious Domains</h4>
            <div className="space-y-3">
              {emailDomainData.slice(0, 4).map((domain, index) => (
                <div key={index} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                  <div className="flex items-center gap-2">
                    <Badge 
                      variant={domain.risk === 'critical' ? 'destructive' : 'secondary'}
                      className="text-xs"
                    >
                      {domain.risk.toUpperCase()}
                    </Badge>
                    <span className="font-mono text-sm">{domain.domain}</span>
                  </div>
                  <span className="font-semibold">{domain.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t">
          <div className="text-center">
            <p className="text-lg font-bold text-red-600">892</p>
            <p className="text-xs text-muted-foreground">Data Center IPs</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-orange-600">312</p>
            <p className="text-xs text-muted-foreground">Sequential Phones</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-yellow-600">23</p>
            <p className="text-xs text-muted-foreground">Suspicious Domains</p>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}